import { Component, inject, signal, OnInit, OnDestroy } from '@angular/core';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatStepperModule } from '@angular/material/stepper';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { QuotaService } from '../../services/quota.service';
import { Quota, SupplierProfile, SupplierCompany, ApplicationSubmitPayload } from '../../models/quota.interface';
import { KgToTonsPipe } from '../../../../shared/pipes/kg-to-tons.pipe';
import { extractApiError, formatApiDate } from '../../../../core/utils/api-format';

@Component({
  selector: 'app-application-submit',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatDialogModule,
    MatStepperModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatButtonModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatCheckboxModule,
    MatSnackBarModule,
    KgToTonsPipe
  ],
  templateUrl: './application-submit.component.html',
  styleUrls: ['./application-submit.component.css']
})
export class ApplicationSubmitComponent implements OnInit, OnDestroy {
  private fb = inject(FormBuilder);
  private quotaService = inject(QuotaService);
  private dialogRef = inject(MatDialogRef<ApplicationSubmitComponent>);
  private data = inject<{ quotaId: number; selectedSupplier: SupplierCompany }>(MAT_DIALOG_DATA);
  private snackBar = inject(MatSnackBar);

  // State signals
  loading = signal(false);
  loadingData = signal(true);
  errorMessage = signal<string | null>(null);
  currentStep = signal(0);
  volumeWarning = signal<string | null>(null);

  // Data
  quota = signal<Quota | null>(null);
  supplierProfile = signal<SupplierProfile | null>(null);

  // Forms
  step1Form!: FormGroup;
  step2Form!: FormGroup;
  step3Form!: FormGroup;

  ngOnInit(): void {
    this.initForms();
    this.loadData();
  }

  ngOnDestroy(): void {}

  private initForms(): void {
    // Step 1 - Company info
    this.step1Form = this.fb.group({
      inn: ['', Validators.required],
      companyName: [''],
      okpo: [''],
      registrationNumber: ['', Validators.required],
      legalStatusId: [null],
      registrationDate: [null, Validators.required]
    });

    // Step 2 - Personal data
    this.step2Form = this.fb.group({
      name: ['', Validators.required],
      pin: ['', Validators.required],
      passportNumber: ['', Validators.required],
      phoneNumber: ['', Validators.required],
      email: ['', Validators.required]
    });

    // Step 3 - Contract info & volume
    this.step3Form = this.fb.group({
      contractName: [''],
      proxyDocumentNumber: ['', Validators.required],
      proxyDocumentDate: [null, Validators.required],
      actingAs: [''],
      contractDate: [null],
      contractNumber: [''],
      vetServiceIssueDate: [null, Validators.required],
      vetServiceIssueNumber: ['', Validators.required],
      requestedVolume: [null, [Validators.required, Validators.min(0.001)]],
      consentToDataProcessing: [false, Validators.requiredTrue]
    });
  }

  private loadData(): void {
    this.loadingData.set(true);

    // Load quota details
    this.quotaService.getQuotaById(this.data.quotaId).subscribe({
      next: (response) => {
        if (response.success) {
          this.quota.set(response.res);
        }
      },
      error: (err) => {
        console.error('Error loading quota:', err);
        this.errorMessage.set('Ошибка загрузки данных квоты');
      }
    });

    if (this.data.selectedSupplier) {
      // Build partial profile from selected supplier + personal data from profile endpoint
      this.quotaService.getSupplierProfile().subscribe({
        next: (response) => {
          const personal = response.success && response.res ? response.res : null;
          const merged = this.mergeSupplierData(this.data.selectedSupplier, personal);
          this.supplierProfile.set(merged);
          this.populateForms(merged);
          this.loadingData.set(false);
        },
        error: () => {
          const merged = this.mergeSupplierData(this.data.selectedSupplier, null);
          this.supplierProfile.set(merged);
          this.populateForms(merged);
          this.loadingData.set(false);
        }
      });
    } else {
      this.quotaService.getSupplierProfile().subscribe({
        next: (response) => {
          if (response.success && response.res) {
            this.supplierProfile.set(response.res);
            this.populateForms(response.res);
          }
          this.loadingData.set(false);
        },
        error: (err) => {
          console.error('Error loading supplier profile:', err);
          this.loadingData.set(false);
        }
      });
    }
  }

  private mergeSupplierData(company: SupplierCompany, personal: SupplierProfile | null): SupplierProfile {
    return {
      id: personal?.id ?? '',
      inn: company.inn,
      companyName: company.companyName,
      okpo: company.okpo,
      registrationNumber: company.registrationNumber,
      registrationDate: company.registrationDate,
      legalStatus: company.legalStatus,
      actingAs: company.actingAs,
      contractNumber: company.contractNumber ?? undefined,
      contractDate: company.contractDate ?? undefined,
      applicantTypeEnum: company.applicantTypeEnum,
      supplierType: company.supplierType,
      // Personal data from profile endpoint (may be empty)
      name: personal?.namePatronymic || personal?.name || '',
      pin: personal?.pin ?? '',
      passportNumber: personal?.passportNumber ?? '',
      phoneNumber: personal?.phoneNumber ?? '',
      email: personal?.email ?? '',
      proxyDocumentNumber: personal?.proxyDocumentNumber,
      proxyDocumentDate: personal?.proxyDocumentDate,
      contractName: personal?.contractName,
      vetServiceIssueDate: personal?.vetServiceIssueDate,
      vetServiceIssueNumber: personal?.vetServiceIssueNumber
    };
  }

  private populateForms(profile: SupplierProfile): void {
    // Step 1
    this.step1Form.patchValue({
      inn: profile.inn,
      companyName: profile.companyName,
      okpo: profile.okpo,
      registrationNumber: profile.registrationNumber,
      legalStatusId: profile.legalStatus?.id,
      registrationDate: profile.registrationDate ? this.parseDateStr(profile.registrationDate) : null
    });

    // Disable pre-filled fields in step 1
    if (profile.inn) this.step1Form.get('inn')?.disable();
    if (profile.companyName) this.step1Form.get('companyName')?.disable();
    if (profile.okpo) this.step1Form.get('okpo')?.disable();
    if (profile.registrationNumber) this.step1Form.get('registrationNumber')?.disable();
    if (profile.legalStatus) this.step1Form.get('legalStatusId')?.disable();
    if (profile.registrationDate) this.step1Form.get('registrationDate')?.disable();

    // Step 2
    this.step2Form.patchValue({
      name: profile.namePatronymic || profile.name,
      pin: profile.pin,
      passportNumber: profile.passportNumber,
      phoneNumber: profile.phoneNumber,
      email: profile.email
    });

    // Disable pre-filled fields in step 2
    if (profile.name) this.step2Form.get('name')?.disable();
    if (profile.pin) this.step2Form.get('pin')?.disable();
    if (profile.passportNumber) this.step2Form.get('passportNumber')?.disable();
    if (profile.phoneNumber) this.step2Form.get('phoneNumber')?.disable();
    if (profile.email) this.step2Form.get('email')?.disable();

    // Step 3
    this.step3Form.patchValue({
      contractName: profile.contractName,
      proxyDocumentNumber: profile.proxyDocumentNumber,
      proxyDocumentDate: profile.proxyDocumentDate ? this.parseDateStr(profile.proxyDocumentDate) : null,
      actingAs: profile.actingAs,
      contractDate: profile.contractDate ? this.parseDateStr(profile.contractDate) : null,
      contractNumber: profile.contractNumber,
      vetServiceIssueDate: profile.vetServiceIssueDate ? this.parseDateStr(profile.vetServiceIssueDate) : null,
      vetServiceIssueNumber: profile.vetServiceIssueNumber
    });

    // Disable only contract-related fields from profile (not vet service - those are per-application)
    if (profile.contractName) this.step3Form.get('contractName')?.disable();
    if (profile.actingAs) this.step3Form.get('actingAs')?.disable();
    if (profile.contractDate) this.step3Form.get('contractDate')?.disable();
    if (profile.contractNumber) this.step3Form.get('contractNumber')?.disable();
    // proxyDocument and vetService fields remain editable for each application
  }

  // Navigation
  next(): void {
    if (this.currentStep() === 0) {
      // Disabled fields don't participate in validation, so check if form is valid or all controls disabled
      const step1Valid = this.step1Form.valid || this.step1Form.disabled ||
        Object.keys(this.step1Form.controls).every(key => this.step1Form.get(key)?.disabled);
      if (step1Valid) {
        this.currentStep.set(1);
      } else {
        this.step1Form.markAllAsTouched();
      }
    } else if (this.currentStep() === 1) {
      // Step 2 is always valid since all fields come from profile
      this.currentStep.set(2);
    }
  }

  prev(): void {
    if (this.currentStep() > 0) {
      this.currentStep.set(this.currentStep() - 1);
    }
  }

  // Volume handling
  onVolumeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = parseFloat(input.value);
    const remainderTons = this.getRemainderTons();

    if (remainderTons !== null && value > remainderTons) {
      this.volumeWarning.set(`Вы превысили доступный объём (${remainderTons} т)`);
    } else {
      this.volumeWarning.set(null);
    }
  }

  // Helpers
  getRemainderTons(): number | null {
    const quota = this.quota();
    if (!quota) return null;
    return this.kgToTons(quota.remainderVolume);
  }

  kgToTons(kg: number): number {
    return Math.round((kg / 1000) * 1000) / 1000;
  }

  tonsToKg(tons: number): number {
    return Math.round(tons * 1000);
  }

  private parseDateStr(date: string): Date {
    // Handle DD.MM.YYYY format returned by the API
    if (/^\d{2}\.\d{2}\.\d{4}$/.test(date)) {
      const [day, month, year] = date.split('.');
      return new Date(+year, +month - 1, +day);
    }
    return new Date(date);
  }

  formatDate(date: Date | string | null | undefined): string | null {
    if (!date) return null;
    const d = typeof date === 'string' ? this.parseDateStr(date) : new Date(date);
    // Check if date is valid
    if (isNaN(d.getTime())) return null;
    return formatApiDate(d);
  }

  isLLC(): boolean {
    return this.supplierProfile()?.applicantTypeEnum === 'LIMITED_LIABILITY_COMPANY';
  }

  showActingAsFields(): boolean {
    return this.isLLC();
  }

  isDirector(): boolean {
    return this.step3Form.get('actingAs')?.value === 'DIRECTOR';
  }

  // Submit
  async handleSubmit(): Promise<void> {
    if (!this.step3Form.valid) {
      this.step3Form.markAllAsTouched();
      return;
    }

    const remainderTons = this.getRemainderTons();
    const requestedVolume = this.step3Form.get('requestedVolume')?.value;

    if (remainderTons !== null && requestedVolume > remainderTons) {
      this.errorMessage.set(`Максимально доступно ${remainderTons} тонн`);
      return;
    }

    this.loading.set(true);
    this.errorMessage.set(null);

    try {
      const payload = this.buildPayload();
      const response = await this.quotaService.submitApplication(payload).toPromise();

      if (response?.success) {
        this.snackBar.open('Вы успешно откликнулись на квоту!', 'Закрыть', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['success-snackbar']
        });
        this.dialogRef.close({ success: true, refresh: true });
      } else {
        this.errorMessage.set(response?.msg || 'Ошибка отправки заявки');
      }
    } catch (error: any) {
      console.error('Submit error:', error);
      const errBody = error?.error;
      const message = extractApiError(errBody, 'Произошла ошибка при отправке заявки');
      this.errorMessage.set(message);
    } finally {
      this.loading.set(false);
    }
  }

  private buildPayload(): ApplicationSubmitPayload {
    const profile = this.supplierProfile()!;
    const quota = this.quota()!;
    const step3 = this.step3Form.getRawValue(); // getRawValue includes disabled fields

    const supplier: any = {
      inn: profile.inn,
      companyName: profile.companyName,
      registrationNumber: profile.registrationNumber,
      contractName: step3.contractName,
      contractNumber: step3.contractNumber || null,
      registrationDate: this.formatDate(profile.registrationDate),
      contractDate: step3.contractDate ? this.formatDate(step3.contractDate) : null,
      vetServiceIssueDate: this.formatDate(step3.vetServiceIssueDate),
      vetServiceIssueNumber: step3.vetServiceIssueNumber,
      passportNumber: profile.passportNumber,
      applicantTypeEnum: profile.applicantTypeEnum,
      supplierType: profile.supplierType
    };

    return {
      quotaId: this.data.quotaId,
      requestedVolume: this.tonsToKg(step3.requestedVolume),
      supplier,
      supplierId: this.data.selectedSupplier?.id ?? profile.id,
      meatType: quota.meatType,
      consentToDataProcessing: step3.consentToDataProcessing,
      vetServiceIssueDate: this.formatDate(step3.vetServiceIssueDate),
      vetServiceIssueNumber: step3.vetServiceIssueNumber,
      veterinaryDocumentStatus: (step3.vetServiceIssueDate || step3.vetServiceIssueNumber) ? 'YES' : null,
      proxyDocumentNumber: step3.proxyDocumentNumber || null,
      proxyDocumentDate: this.formatDate(step3.proxyDocumentDate)
    };
  }

  close(): void {
    this.dialogRef.close();
  }
}
